import type { TutorBillingTableColumn, TutorBillingTableRow } from './interfaces'

import { useListsStore } from '@shared/model'
import { navigateToTutorLegal } from '@features/tutor-billing-return'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import {
  buildTutorBillingFields,
  formatTutorBillingDate,
  getTutorBillingTutorName,
  joinTutorBillingMeta,
} from '../lib/tutor-billing-fields'

interface ModalTutorBillingViewReturn {
  isOpen: ComputedRef<boolean>
  title: ComputedRef<string>
  subtitle: ComputedRef<string | null>
  columns: TutorBillingTableColumn[]
  rows: ComputedRef<TutorBillingTableRow[]>
  setClose: () => void
  setOpenEdit: () => Promise<void>
}

const COLUMNS: TutorBillingTableColumn[] = [
  { key: 'current', label: 'Current' },
]

export const useModalTutorBillingView = (): ModalTutorBillingViewReturn => {
  const store = useTutorBillingStore()
  const listsStore = useListsStore()

  const isOpen = computed<boolean>(() => store.isModal(TutorBillingModal.View))

  const title = computed<string>(() => {
    const row = store.getActiveRow

    return row ? `Billing info ${row.id}` : ''
  })

  const subtitle = computed<string | null>(() => {
    const row = store.getActiveRow
    if (!row) return null

    return joinTutorBillingMeta([
      getTutorBillingTutorName(row),
      row.updated_at ? `last changed ${formatTutorBillingDate(row.updated_at)}` : null,
    ])
  })

  const rows = computed<TutorBillingTableRow[]>(() => {
    const row = store.getActiveRow
    if (!row) return []

    return buildTutorBillingFields(row, listsStore.getTutorBillingPaymentServiceLabel).map(field => ({
      key: field.key,
      label: field.label,
      cells: [{ value: field.value }],
    }))
  })

  const setClose = (): void => {
    store.closeModal()
  }

  const setOpenEdit = async (): Promise<void> => {
    const row = store.getActiveRow
    if (!row) return

    store.closeModal()
    await navigateToTutorLegal(row.tutor.id)
  }

  return {
    isOpen,
    title,
    subtitle,
    columns: COLUMNS,
    rows,
    setClose,
    setOpenEdit,
  }
}
